// Supabase Authentication Service
// Handles Google, Apple, Facebook login

import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

// Get Supabase credentials from environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

const normalizeBaseUrl = (value: string): string => value.replace(/\/+$/, '');

const getAuthRedirectBaseUrl = (): string => {
  const configured = process.env.REACT_APP_SITE_URL?.trim();
  if (configured) return normalizeBaseUrl(configured);
  return normalizeBaseUrl(window.location.origin);
};

const buildAuthCallbackUrl = (nextTestId?: string | null): string => {
  const base = getAuthRedirectBaseUrl();
  const callback = `${base}/auth/callback`;
  if (nextTestId) {
    return `${callback}?nextTestId=${encodeURIComponent(nextTestId)}`;
  }
  return callback;
};

export interface SupabaseUser {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  provider: 'google' | 'apple' | 'facebook' | 'email';
}

class SupabaseAuthService {
  // Sign in with Google
  async signInWithGoogle(): Promise<SupabaseUser | null> {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: buildAuthCallbackUrl(),
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        logger.error('Google sign-in error:', error);
        throw error; // Throw error so it can be caught by caller
      }

      // OAuth redirects to Google immediately - this should redirect the page
      // If data.url exists, the redirect should happen automatically
      if (data?.url) {
        // Redirect will happen automatically via Supabase
        // Don't return null here - let the redirect happen
        window.location.href = data.url;
        return null; // Will be handled by AuthCallback after redirect
      }

      // OAuth redirects to Google, user will be redirected back
      // The actual user data comes from the auth state change
      return null; // Will be handled by onAuthStateChange
    } catch (error) {
      logger.error('Google sign-in error:', error);
      throw error; // Re-throw so caller can handle it
    }
  }

  // Sign in with Apple
  async signInWithApple(): Promise<SupabaseUser | null> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: buildAuthCallbackUrl()
        }
      });

      if (error) {
        logger.error('Apple sign-in error:', error);
        return null;
      }

      return null; // Will be handled by onAuthStateChange
    } catch (error) {
      logger.error('Apple sign-in error:', error);
      return null;
    }
  }

  // Sign in with Facebook
  async signInWithFacebook(): Promise<SupabaseUser | null> {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: buildAuthCallbackUrl()
        }
      });

      if (error) {
        logger.error('Facebook sign-in error:', error);
        return null;
      }

      return null; // Will be handled by onAuthStateChange
    } catch (error) {
      logger.error('Facebook sign-in error:', error);
      return null;
    }
  }

  // Sign up with email and password
  async signUpWithEmail(email: string, password: string, name: string): Promise<{ user: SupabaseUser | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            name: name
          }
        }
      });

      if (error) {
        logger.error('Email sign-up error:', error);
        return { user: null, error };
      }

      if (data.user) {
        const user: SupabaseUser = {
          id: data.user.id,
          email: data.user.email || email,
          name: name,
          provider: 'email'
        };
        return { user, error: null };
      }

      return { user: null, error: new Error('Sign up failed') };
    } catch (error) {
      logger.error('Email sign-up error:', error);
      return { user: null, error: error as Error };
    }
  }

  // Sign in with email and password
  async signInWithEmail(email: string, password: string): Promise<{ user: SupabaseUser | null; error: Error | null }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        logger.error('Email sign-in error:', error);
        return { user: null, error };
      }

      if (data.user) {
        const user: SupabaseUser = {
          id: data.user.id,
          email: data.user.email || email,
          name: data.user.user_metadata?.full_name || data.user.user_metadata?.name || 'User',
          provider: 'email'
        };
        return { user, error: null };
      }

      return { user: null, error: new Error('Sign in failed') };
    } catch (error) {
      logger.error('Email sign-in error:', error);
      return { user: null, error: error as Error };
    }
  }

  // Sign in with magic link (passwordless email)
  async signInWithMagicLink(email: string): Promise<{ error: Error | null }> {
    try {
      // Get nextTestId from localStorage if available
      const nextTestId = localStorage.getItem('pendingNextTestId');
      const redirectUrl = buildAuthCallbackUrl(nextTestId);
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        logger.error('Magic link error:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      logger.error('Magic link error:', error);
      return { error: error as Error };
    }
  }

  /**
   * Request email change (Supabase sends a confirmation link to the new address).
   * For OAuth-only sign-ins, prefer telling users to change email at the provider.
   */
  async requestEmailChange(newEmail: string): Promise<{ error: Error | null }> {
    const trimmed = newEmail.trim();
    if (!trimmed) {
      return { error: new Error('Email required') };
    }
    const { error } = await supabase.auth.updateUser({ email: trimmed });
    if (error) {
      logger.error('updateUser email error', error);
      return { error: new Error(error.message) };
    }
    return { error: null };
  }

  // Sign out
  async signOut(): Promise<void> {
    await supabase.auth.signOut();
  }

  // Get access token for API calls (e.g. verify-payment)
  async getAccessToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  }

  // Get current user (checks session first for automatic login)
  async getCurrentUser(): Promise<SupabaseUser | null> {
    try {
      // Optimized: Use getSession first (faster, cached)
      // For OAuth callbacks, the session should already be available
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logger.error('Session error:', sessionError);
        return null;
      }
      
      if (session?.user) {
        const providerValue = session.user.app_metadata?.provider;
        const provider: 'google' | 'apple' | 'facebook' | 'email' = 
          (providerValue === 'google' || providerValue === 'apple' || providerValue === 'facebook') 
            ? providerValue 
            : 'email';
        
        return {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || 'User',
          avatar_url: session.user.user_metadata?.avatar_url,
          provider
        };
      }

      // Only fallback to getUser if no session (should be rare)
      // This is slower, so we avoid it when possible
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        logger.error('Get user error:', userError);
        return null;
      }
      
      if (user) {
        const providerValue = user.app_metadata?.provider;
        const provider: 'google' | 'apple' | 'facebook' | 'email' = 
          (providerValue === 'google' || providerValue === 'apple' || providerValue === 'facebook') 
            ? providerValue 
            : 'email';
        
        return {
          id: user.id,
          email: user.email || '',
          name: user.user_metadata?.full_name || user.user_metadata?.name || 'User',
          avatar_url: user.user_metadata?.avatar_url,
          provider
        };
      }
      return null;
    } catch (error) {
      logger.error('Error getting current user:', error);
      return null;
    }
  }

  // Listen for auth changes
  onAuthStateChange(callback: (user: SupabaseUser | null) => void) {
    return supabase.auth.onAuthStateChange(async (event: any, session: any) => {
      if (session?.user) {
        const providerValue = session.user.app_metadata?.provider;
        const provider: 'google' | 'apple' | 'facebook' | 'email' = 
          (providerValue === 'google' || providerValue === 'apple' || providerValue === 'facebook') 
            ? providerValue 
            : 'email';
        
        const user: SupabaseUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || 'User',
          avatar_url: session.user.user_metadata?.avatar_url,
          provider
        };
        callback(user);
      } else {
        callback(null);
      }
    });
  }
}

export const supabaseAuth = new SupabaseAuthService();
export { supabase };
